import React from 'react';

import { isAuthError } from '../../api/client';
import type { AdminUserDto, Role } from '../../api/types';
import { getRoleToggleForAdminPage } from '../../domain/admin/roleManagement';
import { canAccessAdmin, roleLabel } from '../../domain/auth/role';
import { getUserDisplayName, getInitialsFromName } from '../../domain/users/display';
import { RoleBadge } from '../../features/members/RoleBadge';
import { adminService } from '../../services/singletons.js';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { uiActions } from '../../store/slices/uiSlice';
import { openTelegramProfileLink } from '../../telegram/telegram';
import { TavernCard, TavernButton, TavernLoading, TavernEmptyState } from '../../ui/components';
import { ConfirmSheet } from '../../ui/ConfirmSheet';

import './AdminPage.css';

type PendingRoleChange = {
  user: AdminUserDto;
  nextRole: Role;
};

export function AdminPage() {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((s) => s.auth.user);

  const [users, setUsers] = React.useState<AdminUserDto[]>([]);
  const [total, setTotal] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const [roleChange, setRoleChange] = React.useState<PendingRoleChange | null>(null);
  const [isSavingForTelegramId, setIsSavingForTelegramId] = React.useState<string | null>(null);

  const canAccess = canAccessAdmin(currentUser?.role);

  const load = React.useCallback(async () => {
    if (!canAccess) return;

    setIsLoading(true);
    setHasError(false);
    try {
      const res = await adminService.listUsers({ limit: 50, offset: 0 });
      setUsers(res.items);
      setTotal(res.total);
    } catch (error) {
      setHasError(true);
      if (!isAuthError(error)) {
        const message =
          error instanceof Error ? error.message : 'Не удалось загрузить пользователей';
        dispatch(uiActions.setNotification({ type: 'error', message }));
      }
    } finally {
      setIsLoading(false);
    }
  }, [canAccess, dispatch]);

  const loadMore = React.useCallback(async () => {
    if (!canAccess) return;
    if (users.length >= total) return;

    setIsLoadingMore(true);
    try {
      const res = await adminService.listUsers({ limit: 50, offset: users.length });
      setUsers((prev) => [...prev, ...res.items]);
      setTotal(res.total);
    } catch (error) {
      if (!isAuthError(error)) {
        const message =
          error instanceof Error ? error.message : 'Не удалось загрузить пользователей';
        dispatch(uiActions.setNotification({ type: 'error', message }));
      }
    } finally {
      setIsLoadingMore(false);
    }
  }, [canAccess, dispatch, total, users.length]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const confirmRoleChange = React.useCallback(async () => {
    if (!roleChange) return;
    if (!canAccess) return;

    const telegramId = roleChange.user.telegramId;
    setIsSavingForTelegramId(telegramId);
    try {
      const res = await adminService.setUserRole({ telegramId, role: roleChange.nextRole });
      setUsers((prev) =>
        prev.map((u) => (u.telegramId === res.telegramId ? { ...u, role: res.role } : u)),
      );
      dispatch(uiActions.setNotification({ type: 'success', message: 'Роль обновлена' }));
      setRoleChange(null);
    } catch (error) {
      if (!isAuthError(error)) {
        const message = error instanceof Error ? error.message : 'Не удалось изменить роль';
        dispatch(uiActions.setNotification({ type: 'error', message }));
      }
    } finally {
      setIsSavingForTelegramId(null);
    }
  }, [canAccess, dispatch, roleChange]);

  return (
    <div className="ttbPage admin-page">
      <h1 className="ttbPageTitle">Админка</h1>

      <div className="ttbPageContent">
        {!currentUser ? (
          <TavernEmptyState title="Нет данных пользователя" icon="⚠️" />
        ) : !canAccess ? (
          <TavernCard>
            <div className="admin-access-denied">
              <div className="admin-access-denied-title">Доступ запрещён</div>
              <div className="admin-access-denied-text">
                Эта страница доступна только администраторам.
              </div>
            </div>
          </TavernCard>
        ) : isLoading ? (
          <TavernLoading />
        ) : hasError ? (
          <div className="admin-content">
            <TavernEmptyState title="Не удалось загрузить пользователей" icon="⚠️" />
            <TavernButton
              variant="secondary"
              onClick={() => {
                void load();
              }}
            >
              Повторить
            </TavernButton>
          </div>
        ) : users.length === 0 ? (
          <TavernEmptyState title="Список пользователей пуст" icon="👥" />
        ) : (
          <div className="admin-users-list">
            {users.map((u) => {
              const name = getUserDisplayName(u);
              const initials = getInitialsFromName(name);
              const isSaving = isSavingForTelegramId === u.telegramId;
              const toggle = getRoleToggleForAdminPage(u.role);
              const canChange = !!toggle?.canChange && !isSaving;
              const actionLabel = toggle?.actionLabel ?? '';

              return (
                <div key={u.telegramId} className="admin-user-item">
                  <div className="mi-avatar-wrap">
                    <div className="mi-avatar">
                      {u.photo_url ? (
                        <img src={u.photo_url} alt={name} />
                      ) : (
                        <span className="member-avatar-placeholder">{initials}</span>
                      )}
                    </div>
                  </div>
                  <RoleBadge role={u.role} />
                  <div className="mi-content">
                    <div className="admin-user-info">
                      <button
                        type="button"
                        onClick={() => {
                          openTelegramProfileLink({
                            telegramId: u.telegramId,
                            username: u.username,
                          });
                        }}
                        className="link-button admin-user-name"
                      >
                        {name}
                      </button>
                      <span className="admin-user-role-text">{roleLabel(u.role)}</span>
                    </div>

                    <TavernButton
                      variant="secondary"
                      disabled={!canChange}
                      onClick={() => {
                        if (!toggle?.canChange) return;
                        setRoleChange({ user: u, nextRole: toggle.nextRole });
                      }}
                    >
                      {isSaving ? 'Сохранение…' : actionLabel}
                    </TavernButton>
                  </div>
                </div>
              );
            })}

            {users.length < total ? (
              <TavernButton
                variant="secondary"
                disabled={isLoadingMore}
                onClick={() => {
                  void loadMore();
                }}
              >
                {isLoadingMore ? 'Загрузка…' : 'Загрузить ещё'}
              </TavernButton>
            ) : null}
          </div>
        )}
      </div>

      <ConfirmSheet
        open={roleChange !== null}
        title="Изменить роль"
        message={
          roleChange ? `Изменить роль пользователя на “${roleLabel(roleChange.nextRole)}”?` : ''
        }
        confirmLabel="Подтвердить"
        onCancel={() => setRoleChange(null)}
        onConfirm={() => {
          void confirmRoleChange();
        }}
      />
    </div>
  );
}
